import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Shield, 
  Dumbbell, 
  User, 
  ClipboardList, 
  Building2, 
  Handshake, 
  Hand, 
  GraduationCap, 
  Map, 
  Heart,
  Users,
  FileSearch,
  Megaphone
} from 'lucide-react';
import type { UserRole } from '@/types';

interface RoleSelectorProps {
  selectedRoles: UserRole[];
  onRoleToggle: (role: UserRole) => void;
  disabled?: boolean;
  className?: string;
}

const AVAILABLE_ROLES: UserRole[] = [
  'admin',
  'coach',
  'customer',
  'examiner',
  'manager',
  'partner',
  'volunteer',
  'instructor',
  'organizer',
  'provider',
  'coordinator',
  'audit',
  'marketer',
];

const ROLE_ICONS = {
  admin: Shield,
  coach: Dumbbell,
  customer: User,
  examiner: ClipboardList,
  manager: Building2,
  partner: Handshake,
  volunteer: Hand,
  instructor: GraduationCap,
  organizer: Map,
  provider: Heart,
  coordinator: Users,
  audit: FileSearch,
  marketer: Megaphone,
};

const ROLE_CARD_COLORS: Record<string, { ring: string; bg: string; icon: string }> = {
  admin: { ring: 'ring-red-400', bg: 'bg-red-50/50', icon: 'bg-red-500 text-white' },
  coach: { ring: 'ring-blue-400', bg: 'bg-blue-50/50', icon: 'bg-blue-500 text-white' },
  customer: { ring: 'ring-green-400', bg: 'bg-green-50/50', icon: 'bg-green-500 text-white' },
  examiner: { ring: 'ring-purple-400', bg: 'bg-purple-50/50', icon: 'bg-purple-500 text-white' },
  manager: { ring: 'ring-orange-400', bg: 'bg-orange-50/50', icon: 'bg-orange-500 text-white' },
  partner: { ring: 'ring-indigo-400', bg: 'bg-indigo-50/50', icon: 'bg-indigo-500 text-white' },
  volunteer: { ring: 'ring-pink-400', bg: 'bg-pink-50/50', icon: 'bg-pink-500 text-white' },
  instructor: { ring: 'ring-teal-400', bg: 'bg-teal-50/50', icon: 'bg-teal-500 text-white' },
  organizer: { ring: 'ring-yellow-400', bg: 'bg-yellow-50/50', icon: 'bg-yellow-600 text-white' },
  provider: { ring: 'ring-gray-400', bg: 'bg-gray-50/50', icon: 'bg-gray-500 text-white' },
  coordinator: { ring: 'ring-cyan-400', bg: 'bg-cyan-50/50', icon: 'bg-cyan-500 text-white' },
  audit: { ring: 'ring-emerald-400', bg: 'bg-emerald-50/50', icon: 'bg-emerald-500 text-white' },
  marketer: { ring: 'ring-fuchsia-400', bg: 'bg-fuchsia-50/50', icon: 'bg-fuchsia-500 text-white' },
};

export function RoleSelector({ selectedRoles, onRoleToggle, disabled, className }: RoleSelectorProps) {
  const { t, dir } = useLanguage();

  return (
    <div className={cn("space-y-3 px-4 pt-2 pb-4", className)}>
      <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-3">
        {AVAILABLE_ROLES.map((role) => {
          const IconComponent = ROLE_ICONS[role];
          const isSelected = selectedRoles.includes(role);
          
          return (
            <Card 
              key={role} 
              className={cn(
                "transition-all duration-200 hover:shadow-md !border-none !shadow-none [border:none!important]",
                isSelected 
                  ? `ring-2 ${ROLE_CARD_COLORS[role].ring} ${ROLE_CARD_COLORS[role].bg}` 
                  : "hover:ring-1 hover:ring-gray-300",
                disabled && "opacity-50"
              )}
            >
              <CardContent className="p-4">
                <div className={cn(
                  "flex items-center gap-4",
                  dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'
                )}>
                  {/* Icon Section */}
                  <div className={cn(
                    "flex items-center justify-center w-12 h-12 rounded-lg transition-all duration-200 flex-shrink-0",
                    isSelected 
                      ? `${ROLE_CARD_COLORS[role].icon} shadow-md` 
                      : "bg-muted"
                  )}>
                    <IconComponent className="h-6 w-6" />
                  </div>
                  
                  {/* Role Info */}
                  <div className="flex-1 min-w-0">
                    <div className={cn(
                      "flex items-center gap-2",
                      dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'
                    )}>
                      <h4 className="font-medium text-sm truncate">
                        {t(`dashboard.admin.users.roles.${role}`)}
                      </h4>
                      {isSelected && (
                        <Badge 
                          variant="secondary" 
                          className="text-xs px-1.5 py-0.5 flex-shrink-0 bg-primary text-white border-none"
                        >
                          {t('dashboard.admin.users.roleManager.active')}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Button */}
                  <Button
                    variant={isSelected ? "outline" : "secondary"}
                    size="sm"
                    onClick={() => !disabled && onRoleToggle(role)}
                    disabled={disabled}
                    className="flex-shrink-0"
                  >
                    {isSelected ? t('dashboard.admin.users.roleManager.remove') : t('dashboard.admin.users.roleManager.assign')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}